import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Loader2, Package, MessageSquare, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Génère ou récupère un visitor_id anonyme
const getVisitorId = () => {
  let visitorId = localStorage.getItem('plus_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('plus_visitor_id', visitorId);
  }
  return visitorId;
};

// Textes d'introduction selon le moment
const MOMENT_TEXTS = {
  reservation: {
    title: 'Préparez votre séjour',
    subtitle: 'Personnalisez votre expérience dès maintenant'
  },
  pre_arrivee: {
    title: 'Votre arrivée approche',
    subtitle: 'Dernière chance pour enrichir votre séjour'
  },
  avant_depart: {
    title: 'Profitez pleinement de vos derniers moments',
    subtitle: 'Des options pour finir en beauté'
  },
  qr_code: {
    title: 'Besoin d\'un petit plus pendant votre séjour ?',
    subtitle: 'Découvrez nos services exclusifs'
  }
};

const PublicProperty = () => {
  const { link } = useParams();
  const [searchParams] = useSearchParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionAnswers, setOptionAnswers] = useState({}); // Réponses aux questions
  const [optionDetails, setOptionDetails] = useState({}); // Détails par option (nb_participants, date_souhaitee)
  const [bookingData, setBookingData] = useState({
    date_debut: '',
    date_fin: '',
    nom_voyageur: '',
    email_voyageur: '',
    telephone_voyageur: '', // Nouveau champ
  });
  const [submitting, setSubmitting] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  
  // Analytics tracking - lecture initiale depuis l'URL
  const [visitorId] = useState(getVisitorId());
  
  // Récupérer moment et source depuis l'URL à chaque render (permet de conserver après refresh)
  const trackingMoment = useMemo(() => {
    const urlMoment = searchParams.get('moment');
    // Valider que le moment est dans les valeurs autorisées
    const validMoments = ['reservation', 'pre_arrivee', 'avant_depart', 'qr_code'];
    return validMoments.includes(urlMoment) ? urlMoment : null;
  }, [searchParams]);
  
  const trackingSource = useMemo(() => {
    const urlSource = searchParams.get('source');
    // Valider que la source est dans les valeurs autorisées
    const validSources = ['airbnb', 'booking', 'pms', 'email', 'qr_code', 'direct'];
    return validSources.includes(urlSource) ? urlSource : null;
  }, [searchParams]);
  
  const [pageViewTracked, setPageViewTracked] = useState(false);
  
  // Texte d'introduction dynamique selon le moment
  const introText = useMemo(() => {
    if (trackingMoment && MOMENT_TEXTS[trackingMoment]) {
      return MOMENT_TEXTS[trackingMoment];
    }
    // Texte par défaut si pas de moment ou moment non reconnu
    return {
      title: 'Améliorez votre séjour',
      subtitle: 'Découvrez nos options exclusives'
    };
  }, [trackingMoment]);

  // Fonction pour envoyer un événement analytics
  const trackEvent = useCallback(async (eventType, optionId = null) => {
    if (!property) return;
    try {
      await axios.post(`${API}/analytics/event`, {
        event_type: eventType,
        property_id: property.id,
        property_link: link,
        option_id: optionId,
        visitor_id: visitorId,
        moment: trackingMoment,
        source: trackingSource
      });
    } catch (error) {
      // Silently fail - analytics shouldn't break UX
      console.warn('Analytics tracking failed:', error);
    }
  }, [property, link, visitorId, trackingMoment, trackingSource]);

  useEffect(() => {
    fetchProperty();
  }, [link]);

  // Track page_view une seule fois quand la propriété est chargée
  useEffect(() => {
    if (property && !pageViewTracked) {
      trackEvent('page_view');
      setPageViewTracked(true);
    }
  }, [property, pageViewTracked, trackEvent]);

  // Désélectionner les options expirées quand la date d'arrivée change
  useEffect(() => {
    if (!property || !bookingData.date_debut) return;
    
    const defaultCheckinTime = property.default_checkin_time || '16:00';
    const [hours, minutes] = defaultCheckinTime.split(':').map(Number);
    const now = new Date();
    
    const expiredOptionIds = property.options
      .filter(option => {
        if (!option.deadline_type || !option.deadline_value) return false;
        
        const arrivalDate = new Date(bookingData.date_debut);
        arrivalDate.setHours(hours, minutes, 0, 0);
        
        let deadline = new Date(arrivalDate);
        if (option.deadline_type === 'hours') {
          deadline.setTime(deadline.getTime() - option.deadline_value * 60 * 60 * 1000);
        } else if (option.deadline_type === 'days') {
          deadline.setTime(deadline.getTime() - option.deadline_value * 24 * 60 * 60 * 1000);
        }
        
        return now >= deadline;
      })
      .map(opt => opt.id);
    
    if (expiredOptionIds.length > 0) {
      setSelectedOptions(prev => prev.filter(id => !expiredOptionIds.includes(id)));
    }
  }, [property, bookingData.date_debut]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/public/property/${link}`);
      setProperty(response.data);
    } catch (error) {
      toast.error('Logement non trouvé');
    } finally {
      setLoading(false);
    }
  };

  const toggleOption = (optionId) => {
    const isCurrentlySelected = selectedOptions.includes(optionId);
    
    // Track l'événement avant de modifier l'état
    if (isCurrentlySelected) {
      trackEvent('cart_remove', optionId);
    } else {
      trackEvent('option_select', optionId);
    }
    
    setSelectedOptions((prev) =>
      prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId]
    );
  };

  const isSelected = (optionId) => selectedOptions.includes(optionId);

  const updateAnswer = (optionId, answer) => {
    setOptionAnswers((prev) => ({ ...prev, [optionId]: answer }));
  };

  const calculateTotal = () => {
    if (!property) return 0;
    return selectedOptions
      .map((id) => {
        const opt = property.options.find((o) => o.id === id);
        if (!opt) return 0;
        
        const globalOption = opt.global_option || {};
        const isPrestataire = globalOption.option_type === 'prestataire';
        const typeTarif = opt.type_tarif || 'personne';
        const prix = opt.prix || 0;
        
        if (isPrestataire && typeTarif === 'personne') {
          // Prix par personne : multiplier par nb_participants
          const details = optionDetails[id] || {};
          const nbParticipants = details.nb_participants || 1;
          return prix * nbParticipants;
        }
        
        // Prix groupe ou option standard : prix fixe
        return prix;
      })
      .reduce((sum, price) => sum + price, 0);
  };

  // Helper pour obtenir le prix affiché d'une option
  const getOptionPriceDisplay = (option) => {
    const globalOption = option.global_option || {};
    const isPrestataire = globalOption.option_type === 'prestataire';
    const typeTarif = option.type_tarif || 'personne';
    const prix = option.prix || 0;
    
    if (isPrestataire) {
      if (typeTarif === 'personne') {
        return `${prix} € / personne`;
      } else {
        let groupLabel = `${prix} € / groupe`;
        if (option.participants_min || option.participants_max) {
          const min = option.participants_min || 1;
          const max = option.participants_max;
          groupLabel += max ? ` (${min}-${max} pers.)` : ` (min. ${min} pers.)`;
        }
        return groupLabel;
      }
    }
    
    return `${prix} €`;
  };

  // Helper pour vérifier si une option prestataire est sélectionnée
  const hasPrestaireOptionSelected = () => {
    if (!property) return false;
    return selectedOptions.some((id) => {
      const opt = property.options.find((o) => o.id === id);
      return opt?.global_option?.option_type === 'prestataire';
    });
  };

  // Vérifier si les achats sont encore disponibles
  // Règle : séjour en cours = OK, séjour terminé = REFUSÉ
  const isPurchaseAvailable = () => {
    if (!bookingData.date_debut || !bookingData.date_fin) return { available: true, message: '' };
    
    const departureDate = new Date(bookingData.date_fin);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const departure = new Date(departureDate.getFullYear(), departureDate.getMonth(), departureDate.getDate());
    
    // Si la date de départ est passée, le séjour est terminé
    if (departure < today) {
      return { available: false, message: "Le séjour est terminé. Les achats ne sont plus possibles après la date de départ." };
    }
    
    return { available: true, message: '' };
  };

  // Calculer le datetime limite pour une option
  const getOptionDeadline = (option) => {
    if (!bookingData.date_debut || !option.deadline_type || !option.deadline_value) {
      return null; // Pas de limite
    }
    
    const defaultCheckinTime = property?.default_checkin_time || '16:00';
    const [hours, minutes] = defaultCheckinTime.split(':').map(Number);
    
    const arrivalDate = new Date(bookingData.date_debut);
    arrivalDate.setHours(hours, minutes, 0, 0);
    
    let deadline = new Date(arrivalDate);
    if (option.deadline_type === 'hours') {
      deadline.setTime(deadline.getTime() - option.deadline_value * 60 * 60 * 1000);
    } else if (option.deadline_type === 'days') {
      deadline.setTime(deadline.getTime() - option.deadline_value * 24 * 60 * 60 * 1000);
    }
    
    return deadline;
  };

  // Vérifier si une option est disponible
  const isOptionAvailable = (option) => {
    if (!bookingData.date_debut) return { available: true, deadline: null };
    
    const deadline = getOptionDeadline(option);
    if (!deadline) return { available: true, deadline: null }; // Pas de limite configurée
    
    const now = new Date();
    return {
      available: now < deadline,
      deadline: deadline
    };
  };

  // Formater une date pour l'affichage
  const formatDeadline = (date) => {
    if (!date) return '';
    const options = { 
      day: 'numeric', 
      month: 'long', 
      hour: '2-digit', 
      minute: '2-digit'
    };
    return date.toLocaleDateString('fr-FR', options);
  };

  const purchaseStatus = isPurchaseAvailable();

  const handleCheckout = async (e) => {
    e.preventDefault();

    if (selectedOptions.length === 0) {
      toast.error('Veuillez sélectionner au moins une option');
      return;
    }

    if (!bookingData.date_debut || !bookingData.date_fin || !bookingData.nom_voyageur || !bookingData.email_voyageur) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    // Vérifier le téléphone si option prestataire sélectionnée
    if (hasPrestaireOptionSelected() && !bookingData.telephone_voyageur) {
      toast.error('Le numéro de téléphone est obligatoire pour réserver une activité');
      return;
    }

    // Vérifier les options prestataire (participants, dates)
    for (const optionId of selectedOptions) {
      const option = property.options.find((opt) => opt.id === optionId);
      const globalOption = option?.global_option;
      
      if (globalOption?.option_type === 'prestataire') {
        const details = optionDetails[optionId] || {};
        const nbParticipants = details.nb_participants || 1;
        
        // Vérifier le nombre de participants
        if (option.participants_min && nbParticipants < option.participants_min) {
          toast.error(`Minimum ${option.participants_min} participant(s) pour "${globalOption.nom}"`);
          return;
        }
        if (option.participants_max && nbParticipants > option.participants_max) {
          toast.error(`Maximum ${option.participants_max} participant(s) pour "${globalOption.nom}"`);
          return;
        }
        
        // Vérifier la date souhaitée
        if (!details.date_souhaitee) {
          toast.error(`Veuillez choisir une date pour "${globalOption.nom}"`);
          return;
        }
        
        // Vérifier que la date est dans les dates du séjour
        const activityDate = new Date(details.date_souhaitee);
        const arrivalDate = new Date(bookingData.date_debut);
        const departureDate = new Date(bookingData.date_fin);
        
        if (activityDate < arrivalDate || activityDate > departureDate) {
          toast.error(`La date de l'activité "${globalOption.nom}" doit être comprise entre vos dates de séjour`);
          return;
        }
      }
    }

    // Vérifier l'acceptation de la politique de confidentialité
    if (!privacyAccepted) {
      toast.error('Veuillez accepter la politique de confidentialité pour continuer.');
      return;
    }

    // Vérifier la limite de temps
    const { available, message } = isPurchaseAvailable();
    if (!available) {
      toast.error(message);
      return;
    }

    // Vérifier que les options sélectionnées sont encore disponibles
    const expiredOptions = [];
    for (const optionId of selectedOptions) {
      const option = property.options.find((opt) => opt.id === optionId);
      if (option) {
        const availability = isOptionAvailable(option);
        if (!availability.available) {
          expiredOptions.push(option.global_option?.nom || 'Option');
        }
      }
    }
    if (expiredOptions.length > 0) {
      toast.error(`Ces options ne sont plus disponibles : ${expiredOptions.join(', ')}. Veuillez les retirer de votre sélection.`);
      return;
    }

    // Vérifier que toutes les questions obligatoires ont une réponse
    for (const optionId of selectedOptions) {
      const option = property.options.find((opt) => opt.id === optionId);
      const globalOption = option?.global_option;
      if (globalOption?.question_enabled && globalOption?.question_text) {
        if (!optionAnswers[optionId] || optionAnswers[optionId].trim() === '') {
          toast.error(`Veuillez répondre à la question pour "${globalOption.nom}"`);
          return;
        }
      }
    }

    setSubmitting(true);

    try {
      const checkoutPayload = {
        property_link: link,
        date_debut: bookingData.date_debut,
        date_fin: bookingData.date_fin,
        nom_voyageur: bookingData.nom_voyageur,
        email_voyageur: bookingData.email_voyageur,
        telephone_voyageur: bookingData.telephone_voyageur || null,
        selected_options: selectedOptions,
        option_answers: optionAnswers,
        option_details: optionDetails,
        origin_url: window.location.origin,
        // Analytics tracking
        visitor_id: visitorId,
        moment: trackingMoment,
        source: trackingSource,
      };

      const response = await axios.post(`${API}/checkout`, checkoutPayload);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center px-4">
        <Card className="bg-white p-8 sm:p-12 text-center max-w-md w-full">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Logement non trouvé</h2>
          <p className="text-sm sm:text-base text-slate-600">Ce logement n'existe pas ou n'est plus disponible.</p>
        </Card>
      </div>
    );
  }

  const total = calculateTotal();

  return (
    <div className="min-h-screen bg-slate-50/50 pb-40 sm:pb-32">
      {/* Hero Section - Responsive height */}
      <div 
        className="relative h-[250px] sm:h-[350px] md:h-[400px] bg-gradient-to-b from-black/60 to-black/20" 
        style={{ 
          backgroundImage: property.image_url 
            ? `url(${property.image_url})` 
            : 'url(https://images.unsplash.com/photo-1758548157747-285c7012db5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMG1vZGVybiUyMGFwYXJ0bWVudCUyMGludGVyaW9yJTIwZGVzaWdufGVufDB8fHx8MTc3MTg2OTExN3ww&ixlib=rb-4.1.0&q=85)', 
          backgroundSize: 'cover', 
          backgroundPosition: 'center' 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/20"></div>
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{property.nom}</h1>
            <p className="text-sm sm:text-base md:text-lg text-white/90">{property.adresse}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-12">
        {/* Booking Form */}
        <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-900 mb-4 sm:mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Informations de séjour</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label htmlFor="date_debut" className="text-sm font-medium">Date d'arrivée</Label>
              <Input
                id="date_debut"
                type="date"
                value={bookingData.date_debut}
                onChange={(e) => setBookingData({ ...bookingData, date_debut: e.target.value })}
                required
                data-testid="booking-date-debut-input"
                className="mt-1 h-11 text-base"
                style={{ minHeight: '44px' }}
              />
            </div>
            <div>
              <Label htmlFor="date_fin" className="text-sm font-medium">Date de départ</Label>
              <Input
                id="date_fin"
                type="date"
                value={bookingData.date_fin}
                onChange={(e) => setBookingData({ ...bookingData, date_fin: e.target.value })}
                required
                data-testid="booking-date-fin-input"
                className="mt-1 h-11 text-base"
                style={{ minHeight: '44px' }}
              />
            </div>
            <div>
              <Label htmlFor="nom_voyageur" className="text-sm font-medium">Nom complet</Label>
              <Input
                id="nom_voyageur"
                value={bookingData.nom_voyageur}
                onChange={(e) => setBookingData({ ...bookingData, nom_voyageur: e.target.value })}
                required
                data-testid="booking-nom-input"
                className="mt-1 h-11 text-base"
                style={{ minHeight: '44px' }}
              />
            </div>
            <div>
              <Label htmlFor="email_voyageur" className="text-sm font-medium">Email</Label>
              <Input
                id="email_voyageur"
                type="email"
                value={bookingData.email_voyageur}
                onChange={(e) => setBookingData({ ...bookingData, email_voyageur: e.target.value })}
                required
                data-testid="booking-email-input"
                className="mt-1 h-11 text-base"
                style={{ minHeight: '44px' }}
              />
            </div>
            
            {/* Téléphone - obligatoire si activité prestataire sélectionnée */}
            {hasPrestaireOptionSelected() && (
              <div>
                <Label htmlFor="telephone_voyageur" className="text-sm font-medium flex items-center gap-1">
                  Téléphone *
                  <span className="text-xs text-purple-600 font-normal">(requis pour les activités)</span>
                </Label>
                <Input
                  id="telephone_voyageur"
                  type="tel"
                  value={bookingData.telephone_voyageur}
                  onChange={(e) => setBookingData({ ...bookingData, telephone_voyageur: e.target.value })}
                  placeholder="+33 6 12 34 56 78"
                  required
                  data-testid="booking-phone-input"
                  className="mt-1 h-11 text-base"
                  style={{ minHeight: '44px' }}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Votre numéro sera utilisé uniquement pour confirmer votre réservation d'activité
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Options */}
        <div className="mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="options-title">
            {introText.title}
          </h2>
          <p className="text-sm sm:text-base text-slate-600 mt-1" data-testid="options-subtitle">
            {introText.subtitle}
          </p>
        </div>
        
        {property.options.length === 0 ? (
          <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-8 sm:p-12 text-center" data-testid="empty-options-state">
            <Package className="w-12 sm:w-16 h-12 sm:h-16 text-slate-300 mx-auto mb-3 sm:mb-4" strokeWidth={1.5} />
            <h3 className="text-lg sm:text-xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Aucune option disponible</h3>
            <p className="text-sm text-slate-500">Aucune option premium n'est actuellement disponible pour ce logement.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8" data-testid="options-grid">
            {property.options.map((option) => {
              const selected = isSelected(option.id);
              const globalOption = option.global_option || {};
              // Priorité : image personnalisée concierge > image globale admin
              const imageUrl = option.image_url || globalOption.image_url;
              const description = option.description_custom || globalOption.description || '';
              const availability = isOptionAvailable(option);
              const isExpired = !availability.available;
              
              return (
                <Card
                  key={option.id}
                  data-testid={`option-card-${option.id}`}
                  className={`group relative overflow-hidden rounded-xl bg-white border shadow-sm transition-all ${
                    isExpired 
                      ? 'border-slate-200 opacity-60 cursor-not-allowed' 
                      : selected 
                        ? 'border-rose-600 ring-2 ring-rose-600/20 bg-rose-50/30 cursor-pointer hover:shadow-md' 
                        : 'border-slate-100 cursor-pointer hover:shadow-md'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isExpired) {
                      toggleOption(option.id);
                    }
                  }}
                >
                  {/* Badge expirée */}
                  {isExpired && (
                    <div className="absolute top-3 right-3 z-10 bg-slate-800 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Non disponible
                    </div>
                  )}
                  
                  {/* Image */}
                  {imageUrl && (
                    <div className={`w-full h-32 sm:h-40 overflow-hidden bg-slate-100 ${isExpired ? 'grayscale' : ''}`}>
                      <img 
                        src={imageUrl} 
                        alt={globalOption.nom}
                        className={`w-full h-full object-cover transition-transform duration-300 ${!isExpired ? 'group-hover:scale-105' : ''}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 pr-2">
                        <h3 className={`text-base sm:text-lg md:text-xl font-medium mb-1 sm:mb-2 ${isExpired ? 'text-slate-500' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {globalOption.nom}
                        </h3>
                        <p className={`text-xs sm:text-sm line-clamp-2 ${isExpired ? 'text-slate-400' : 'text-slate-600'}`}>{description}</p>
                      </div>
                      {!isExpired && (
                        <div 
                          className="ml-2 mt-1 flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleOption(option.id);
                          }}
                        >
                          <Checkbox
                            checked={selected}
                            data-testid={`option-checkbox-${option.id}`}
                            className="w-5 h-5"
                          />
                        </div>
                      )}
                    </div>

                    <div className={`p-2 sm:p-3 rounded-lg ${
                      isExpired ? 'bg-slate-100' : selected ? 'bg-rose-600' : 'bg-rose-50'
                    }`}>
                      <p className={`text-xl sm:text-2xl font-bold tabular-nums ${
                        isExpired ? 'text-slate-400' : selected ? 'text-white' : 'text-rose-600'
                      }`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {getOptionPriceDisplay(option)}
                      </p>
                    </div>

                    {/* Champs spécifiques pour les activités prestataire */}
                    {!isExpired && globalOption.option_type === 'prestataire' && selected && (
                      <div 
                        className="p-3 rounded-lg border bg-purple-50 border-purple-200 space-y-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <p className="text-sm font-medium text-purple-800 flex items-center gap-1">
                          🎯 Détails de votre activité
                        </p>
                        
                        {/* Nombre de participants */}
                        <div>
                          <Label className="text-xs text-purple-700">
                            Nombre de participants *
                            {option.participants_min || option.participants_max ? (
                              <span className="text-purple-500 ml-1">
                                ({option.participants_min ? `min. ${option.participants_min}` : ''}
                                {option.participants_min && option.participants_max ? ', ' : ''}
                                {option.participants_max ? `max. ${option.participants_max}` : ''})
                              </span>
                            ) : null}
                          </Label>
                          <Input
                            type="number"
                            min={option.participants_min || 1}
                            max={option.participants_max || undefined}
                            value={optionDetails[option.id]?.nb_participants || 1}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 1;
                              setOptionDetails(prev => ({
                                ...prev,
                                [option.id]: {
                                  ...prev[option.id],
                                  nb_participants: val
                                }
                              }));
                            }}
                            className="mt-1 bg-white"
                            data-testid={`option-participants-${option.id}`}
                          />
                          {/* Affichage du prix total si tarif par personne */}
                          {option.type_tarif === 'personne' && (optionDetails[option.id]?.nb_participants || 1) > 1 && (
                            <p className="text-xs text-purple-600 mt-1">
                              Total: {option.prix * (optionDetails[option.id]?.nb_participants || 1)} €
                            </p>
                          )}
                        </div>
                        
                        {/* Date souhaitée */}
                        <div>
                          <Label className="text-xs text-purple-700">
                            Date souhaitée pour l'activité *
                          </Label>
                          <Input
                            type="date"
                            min={bookingData.date_debut || undefined}
                            max={bookingData.date_fin || undefined}
                            value={optionDetails[option.id]?.date_souhaitee || ''}
                            onChange={(e) => {
                              setOptionDetails(prev => ({
                                ...prev,
                                [option.id]: {
                                  ...prev[option.id],
                                  date_souhaitee: e.target.value
                                }
                              }));
                            }}
                            className="mt-1 bg-white"
                            data-testid={`option-date-${option.id}`}
                          />
                          {!bookingData.date_debut && (
                            <p className="text-xs text-orange-600 mt-1">
                              Renseignez vos dates de séjour ci-dessus d'abord
                            </p>
                          )}
                        </div>
                        
                        <p className="text-xs text-purple-500">
                          ⏳ Cette activité nécessite une confirmation auprès du prestataire
                        </p>
                      </div>
                    )}

                    {/* Affichage du délai */}
                    {option.deadline_type && option.deadline_value && bookingData.date_debut && (
                      <div className={`p-2 rounded-lg text-xs flex items-center gap-1 ${
                        isExpired 
                          ? 'bg-red-50 text-red-600 border border-red-100' 
                          : 'bg-orange-50 text-orange-700 border border-orange-100'
                      }`}>
                        <Clock className="w-3 h-3" />
                        {isExpired 
                          ? "Cette option n'est plus disponible pour ce séjour"
                          : `Disponible jusqu'au ${formatDeadline(availability.deadline)}`
                        }
                      </div>
                    )}

                    {/* Question pour le voyageur - affichée seulement si option disponible */}
                    {/* Priorité : question personnalisée du concierge > question globale admin */}
                    {!isExpired && (option.question_text || (globalOption.question_enabled && globalOption.question_text)) && (
                      <div 
                        className={`p-3 rounded-lg border ${selected ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Label className={`text-sm font-medium flex items-center gap-2 mb-2 ${selected ? 'text-blue-800' : 'text-slate-600'}`}>
                          <MessageSquare className="w-4 h-4" />
                          {option.question_text || globalOption.question_text}
                        </Label>
                        <Textarea
                          value={optionAnswers[option.id] || ''}
                          onChange={(e) => updateAnswer(option.id, e.target.value)}
                          placeholder="Votre réponse..."
                          className={`mt-1 text-sm bg-white ${!selected ? 'opacity-60' : ''}`}
                          rows={2}
                          data-testid={`option-answer-${option.id}`}
                          disabled={!selected}
                        />
                        {!selected && (
                          <p className="text-xs text-slate-400 mt-1 italic">Sélectionnez cette option pour répondre</p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {selected && (
                    <div className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-rose-600 text-white px-2 sm:px-3 py-1 rounded-full text-xs font-medium">
                      ✓ Sélectionné
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Sticky Bottom Bar - Mobile optimized */}
        {property.options.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-xl p-3 sm:p-4 z-50">
            <div className="max-w-6xl mx-auto">
              {/* Checkbox consentement */}
              <div className="flex items-start space-x-2 mb-3 px-1">
                <Checkbox
                  id="privacy-consent"
                  checked={privacyAccepted}
                  onCheckedChange={(checked) => setPrivacyAccepted(checked)}
                  data-testid="privacy-consent-checkbox"
                  className="mt-0.5"
                />
                <label htmlFor="privacy-consent" className="text-xs sm:text-sm text-slate-600 cursor-pointer leading-tight">
                  J'ai lu et j'accepte la{' '}
                  <Link 
                    to="/politique-de-confidentialite" 
                    target="_blank"
                    className="text-rose-600 hover:underline font-medium"
                  >
                    politique de confidentialité
                  </Link>
                </label>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                <div className="text-center sm:text-left w-full sm:w-auto">
                  <p className="text-xs sm:text-sm text-slate-600">{selectedOptions.length} option(s) sélectionnée(s)</p>
                  <p className="text-2xl sm:text-3xl font-bold text-rose-600 tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="total-amount">
                    {total.toFixed(2)} €
                  </p>
                </div>
                <Button
                  onClick={handleCheckout}
                  disabled={submitting || selectedOptions.length === 0 || !privacyAccepted}
                  data-testid="checkout-btn"
                  className="w-full sm:w-auto bg-rose-600 hover:bg-rose-700 text-white shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 rounded-lg px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Traitement...</>
                  ) : (
                    selectedOptions.length === 0 ? 'Sélectionnez des options' : `Payer ${total.toFixed(2)} €`
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Footer avec liens légaux */}
        <footer className="mt-8 pb-32 sm:pb-24">
          <div className="border-t border-slate-100 pt-6">
            <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 text-xs sm:text-sm text-slate-500">
              <Link to="/politique-de-confidentialite" className="hover:text-rose-600 transition-colors">
                Politique de confidentialité
              </Link>
              <span className="hidden sm:inline">•</span>
              <Link to="/mentions-legales" className="hover:text-rose-600 transition-colors">
                Mentions légales
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PublicProperty;
